import { Component } from '@theme/component';
import { fetchConfig, debounce, resetShimmer } from '@theme/utilities';
import { morphSection } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

// A cart page and drawer can coexist. Keep every checkout path gated until all local edits
// have an authoritative response, including the 300ms before a quantity request starts.
const pendingCarts = new Set();
let cartChangeQueue = Promise.resolve();
let cartStructureVersion = 0;
const externalCartEvents = new WeakSet();
const checkoutStates = new Map();
const checkoutSelector = '.cart-remove-cards__button, [name="checkout"], .additional-checkout-buttons, shopify-accelerated-checkout-cart';
const gateCheckoutControls = () => {
  for (const element of document.querySelectorAll(checkoutSelector)) {
    if (!checkoutStates.has(element)) checkoutStates.set(element, {
      inert: element.inert,
      ariaDisabled: element.getAttribute('aria-disabled'),
      ariaBusy: element.getAttribute('aria-busy'),
    });
    if (!element.inert) element.inert = true;
    element.setAttribute('aria-disabled', 'true');
    element.setAttribute('aria-busy', 'true');
    element.classList.add('cart-checkout-pending');
  }
};
const checkoutObserver = new MutationObserver(gateCheckoutControls);
const guardCheckout = (event) => {
  if (!pendingCarts.size) return;
  const target = event.submitter || event.target;
  const control = target instanceof Element ? target.closest(`${checkoutSelector}, a[href]`) : null;
  const href = control?.getAttribute('href');
  const destination = href ? new URL(href, window.location.href) : null;
  const checkoutLink = destination?.origin === window.location.origin && /\/(?:checkout|checkouts)(?:\/|$)/.test(destination.pathname);
  const cartSubmit = event.type === 'submit' && event.target instanceof Element && event.target.matches('.cart-form, .cart-remove-cards');
  if (!cartSubmit && !control?.matches(checkoutSelector) && !checkoutLink) return;
  event.preventDefault();
  event.stopImmediatePropagation();
};
function setCartPending(component, pending) {
  if (pending) {
    if (!pendingCarts.size) {
      document.addEventListener('click', guardCheckout, true);
      document.addEventListener('submit', guardCheckout, true);
      checkoutObserver.observe(document.documentElement, { childList: true, subtree: true, attributes: true, attributeFilter: ['inert'] });
    }
    pendingCarts.add(component);
    gateCheckoutControls();
  } else {
    pendingCarts.delete(component);
    if (pendingCarts.size) return;
    checkoutObserver.disconnect();
    document.removeEventListener('click', guardCheckout, true);
    document.removeEventListener('submit', guardCheckout, true);
    for (const [element, state] of checkoutStates) {
      element.inert = state.inert;
      for (const [attribute, value] of [['aria-disabled', state.ariaDisabled], ['aria-busy', state.ariaBusy]]) {
        if (value === null) element.removeAttribute(attribute);
        else element.setAttribute(attribute, value);
      }
      element.classList.remove('cart-checkout-pending');
    }
    checkoutStates.clear();
  }
}

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedFlush = debounce(() => this.#flushUpdates(), 300);
  #updating = false;
  #removing = false;
  #recoveryRequired = false;
  #reconciling = false;
  #externalRevision = 0;
  #renderedRevision = 0;

  /** @type {Map<string, {key: string, line: number, quantity: number, action: string, version: number}>} */
  #pendingUpdates = new Map();

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityChange);
    // A running request owns its checkout gate until its response/recovery has finished.
    if (!this.#updating && !this.#recoveryRequired && !this.#reconciling) {
      this.#debouncedFlush.cancel();
      this.#pendingUpdates.clear();
      setCartPending(this, false);
    }
  }

  /** Capture the row key before debounce: discounts can split/reorder the server's rows meanwhile.
   * @param {QuantitySelectorUpdateEvent} event
   */
  #onQuantityChange = (event) => {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;
    const { quantity, cartLine: line } = event.detail;
    if (!line || this.#removing || this.#recoveryRequired || this.#reconciling) return;
    if (quantity === 0) return this.onLineItemRemove(line);
    if (!this.#queueUpdate({ line, quantity, action: 'change' })) return;
    this.#debouncedFlush();
    const row = this.refs.cartItemRows[line - 1];
    const text = /** @type {TextComponent | undefined} */ (row?.querySelector('text-component'));
    text?.shimmer();
  };

  /** @param {number} line */
  onLineItemRemove(line) {
    // Keep rows in place until Shopify confirms removal; their displayed line numbers must not
    // shift underneath another control. The request still targets only this row's exact key.
    if (this.#removing || this.#recoveryRequired || this.#reconciling) return;
    this.updateQuantity({ line, quantity: 0, action: 'clear' });
  }

  /** @param {{line: number, quantity: number, action: string}} config */
  #queueUpdate(config) {
    if (this.#removing || this.#recoveryRequired || this.#reconciling) return false;
    const key = this.refs.cartItemRows[config.line - 1]?.dataset.key;
    if (!key) return false;
    this.#pendingUpdates.set(key, { ...config, key, version: cartStructureVersion });
    this.#removing = config.action === 'clear';
    if (this.#removing) this.#disableCartItems();
    this.classList.add('cart-items-busy');
    this.setAttribute('aria-busy', 'true');
    setCartPending(this, true);
    return true;
  }

  /** @param {{line: number, quantity: number, action: string}} config */
  updateQuantity(config) {
    if (this.#queueUpdate(config)) this.#flushUpdates();
  }

  async #flushUpdates() {
    if (this.#updating || !this.#pendingUpdates.size) return;
    this.#updating = true;
    const previous = cartChangeQueue;
    let release;
    cartChangeQueue = new Promise((resolve) => { release = resolve; });
    await previous;
    const marker = cartPerformance.createStartingMarker('change:user-action');
    const sections = new Set([this.sectionId]);
    for (const item of document.querySelectorAll('cart-items-component')) {
      if (item instanceof HTMLElement && item.dataset.sectionId) sections.add(item.dataset.sectionId);
    }
    let response;
    let changedKey = false;
    let failureMessage;
    try {
      while (this.#pendingUpdates.size) {
        const next = this.#pendingUpdates.values().next().value;
        this.#pendingUpdates.delete(next.key);
        // Keys can themselves change when Shopify applies a discount. Never guess which of the
        // new rows should inherit an edit, particularly where properties or selling plans differ.
        if (next.version !== cartStructureVersion || (response && !response.items.some((item) => item.key === next.key))) {
          changedKey = true;
          continue;
        }
        const keysBefore = new Set(response?.items.map((item) => item.key) || this.refs.cartItemRows.map((row) => row.dataset.key));
        this.refs.cartTotal?.shimmer();
        const body = JSON.stringify({
          id: next.key,
          quantity: next.quantity,
          sections: Array.from(sections).join(','),
          sections_url: window.location.pathname,
        });
        const result = await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body }));
        const cart = JSON.parse(await result.text());
        if (cart.errors || !Array.isArray(cart.items)) {
          failureMessage = typeof cart.errors === 'string' ? cart.errors : undefined;
          throw new Error('Cart quantity update was not confirmed');
        }
        const keysChanged = cart.items.length !== keysBefore.size || cart.items.some((item) => !keysBefore.has(item.key));
        const returnedQuantity = cart.items.find((item) => item.key === next.key)?.quantity || 0;
        if (keysChanged || returnedQuantity !== next.quantity) {
          // Even a surviving key may now be only one part of a discounted selection. Stop older
          // edits across both views rather than interpreting an intended total as one child's qty.
          cartStructureVersion += 1;
        }
        response = cart;
        // Do not render an intermediate response over newer input. The queue is populated at the
        // original event, not after debounce, so even a delayed cross-row edit is already captured.
      }
      if (response && [...sections].some((sectionId) => !response.sections?.[sectionId])) throw new Error('Basket section missing');
      if (response && this.#externalRevision === this.#renderedRevision) await this.#renderCartResponse(response);
      if (!response || this.#externalRevision !== this.#renderedRevision) await this.#refreshBasket(sections);
      if (changedKey) this.#showUpdateMessage('Your basket changed while you were editing. Check the quantities below and try that change again.');
    } catch (error) {
      console.error(error);
      this.#debouncedFlush.cancel();
      this.#pendingUpdates.clear();
      try {
        // Do not retry an uncertain mutation. Refresh all basket views before enabling checkout.
        await this.#refreshBasket(sections);
        this.#showUpdateMessage(failureMessage || this.dataset.updateErrorLabel || 'We couldn’t update your basket. Check the refreshed quantities and try again.');
      } catch (refreshError) {
        console.error(refreshError);
        this.#recoveryRequired = true;
        this.#showUpdateMessage('We couldn’t confirm your basket. Refresh it before checking out.', true);
      }
    } finally {
      this.#updating = false;
      this.#removing = false;
      this.#reconciling = false;
      resetShimmer(this);
      this.classList.remove('cart-items-busy');
      this.#enableCartItems();
      this.removeAttribute('aria-busy');
      cartPerformance.measureFromMarker(marker);
      // A failed refresh retains the checkout gate. A full reload is the safe recovery path.
      if (!this.#recoveryRequired && !this.#pendingUpdates.size) setCartPending(this, false);
      release();
      if (this.#pendingUpdates.size) this.#flushUpdates();
    }
  }

  /** @param {Set<string>} sections @param {boolean} [notify] */
  async #refreshBasket(sections, notify = true) {
    this.#reconciling = true;
    this.#disableCartItems();
    // An add/discount request outside this component may finish while our older request is in
    // flight. Never let that older response become the checkout view. Re-read after that event.
    let revision;
    do {
      revision = this.#externalRevision;
      const refreshed = {};
      for (const sectionId of sections) {
        const url = new URL(window.location.href);
        url.searchParams.set('section_id', sectionId);
        const result = await fetch(url, { cache: 'no-store' });
        if (!result.ok) throw new Error('Basket refresh failed');
        const html = await result.text();
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        if (!parsed.getElementById(`shopify-section-${sectionId}`)) throw new Error('Basket refresh failed');
        refreshed[sectionId] = html;
      }
      await this.#renderCartResponse({ sections: refreshed }, notify);
    } while (revision !== this.#externalRevision);
    this.#renderedRevision = revision;
  }

  /** @param {{items?: Array, sections: Record<string, string>}} cart @param {boolean} [notify] */
  async #renderCartResponse(cart, notify = true) {
    const html = cart?.sections?.[this.sectionId];
    if (!html) throw new Error('Basket section missing');
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const itemCount = Number.parseInt(parsed.querySelector('[ref="cartItemCount"]')?.textContent || '0', 10);
    this.#updateQuantitySelectors(cart);
    // Await every view's morph before releasing checkout, including the page's sticky summary.
    for (const [sectionId, sectionHTML] of Object.entries(cart.sections)) {
      if (!sectionHTML) throw new Error('Basket section missing');
      await morphSection(sectionId, sectionHTML, { mode: sectionId === this.sectionId && this.isDrawer ? 'hydration' : 'full' });
    }
    if (notify) this.dispatchEvent(new CartUpdateEvent(cart, this.sectionId, {
      itemCount, source: 'cart-items-component', sections: cart.sections,
    }));
    this.#updateCartQuantitySelectorButtonStates();
  }

  /** @param {string} message @param {boolean} [refresh] */
  #showUpdateMessage(message, refresh = false) {
    // A removed/re-keyed row may no longer have an error ref. Keep the explanation visible at the
    // basket level, rather than attaching it to whichever item inherited the old line number.
    const current = document.querySelector(`cart-items-component[data-section-id="${this.sectionId}"]`) || this;
    let notice = current.querySelector('[data-cart-update-notice]');
    if (!notice) {
      notice = document.createElement('p');
      notice.setAttribute('data-cart-update-notice', '');
      notice.setAttribute('role', 'status');
      current.prepend(notice);
    }
    notice.textContent = message;
    if (refresh) {
      const link = document.createElement('a');
      link.href = window.location.href;
      link.textContent = ' Refresh basket';
      notice.append(link);
    }
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event.target === this) return;
    if (event.detail?.data?.source !== 'cart-items-component' && !externalCartEvents.has(event)) {
      externalCartEvents.add(event);
      cartStructureVersion += 1;
    }
    if (this.#updating || this.#pendingUpdates.size || this.#recoveryRequired || this.#reconciling) {
      this.#externalRevision += 1;
      this.#reconciling = true;
      this.#disableCartItems();
      return;
    }
    const cartItemsHtml = event instanceof DiscountUpdateEvent ? null : event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      this.#refreshExternalCart();
    }
  };

  async #refreshExternalCart() {
    this.#reconciling = true;
    setCartPending(this, true);
    try {
      // A discount/add event may need a GET before its new row structure is known. Freeze this
      // view even when there was no local edit yet, and avoid rebroadcast loops between views.
      await this.#refreshBasket(new Set([this.sectionId]), false);
    } catch (error) {
      console.error(error);
      this.#recoveryRequired = true;
      this.#showUpdateMessage('We couldn’t confirm your basket. Refresh it before checking out.', true);
    } finally {
      this.#reconciling = false;
      this.#enableCartItems();
      if (!this.#recoveryRequired) setCartPending(this, false);
    }
  }

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
